import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Squares2X2Icon,
  UsersIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  DocumentCurrencyDollarIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Container, PillLink, Reveal, shot, usePrefersReducedMotion } from './primitives';

const SCREENS = [
  { id: 'dashboard', icon: Squares2X2Icon },
  { id: 'clients', icon: UsersIcon },
  { id: 'projects', icon: FolderIcon },
  { id: 'tasks', icon: ClipboardDocumentListIcon },
  { id: 'invoices', icon: DocumentCurrencyDollarIcon },
  { id: 'calendar', icon: CalendarDaysIcon }
];

const AUTOPLAY_SECONDS = 6;

function ProductShowcase() {
  const { t, i18n } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set([0]));
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(true);
  const [autoplay, setAutoplay] = useState(true);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const tabRefs = useRef([]);
  const isRtl = i18n.dir() === 'rtl';

  // Mount the active screen and warm up the next one.
  useEffect(() => {
    setLoaded((previous) => {
      const next = new Set(previous);
      next.add(active);
      next.add((active + 1) % SCREENS.length);
      return next.size === previous.size ? previous : next;
    });
  }, [active]);

  // Pause autoplay while the showcase is off screen.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Keep the active tab visible inside the horizontally scrolling tab bar (without moving the page).
  useEffect(() => {
    const list = listRef.current;
    const tab = tabRefs.current[active];
    if (!list || !tab) return;
    const listBox = list.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    if (tabBox.left < listBox.left || tabBox.right > listBox.right) {
      list.scrollBy({
        left: tabBox.left + tabBox.width / 2 - (listBox.left + listBox.width / 2),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }
  }, [active, reducedMotion]);

  const select = useCallback((index, { focus = false, manual = true } = {}) => {
    setActive(index);
    if (manual) setAutoplay(false);
    if (focus) tabRefs.current[index]?.focus();
  }, []);

  const onKeyDown = (event) => {
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';
    const last = SCREENS.length - 1;
    let next = null;
    if (event.key === forward) next = active === last ? 0 : active + 1;
    if (event.key === backward) next = active === 0 ? last : active - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next !== null) {
      event.preventDefault();
      select(next, { focus: true });
    }
  };

  const running = autoplay && !reducedMotion;
  const paused = hovered || !inView;

  return (
    <div
      ref={rootRef}
      className={paused ? 'lp-paused' : ''}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div ref={listRef} className="overflow-x-auto rounded-md bg-sand-100 p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          role="tablist"
          aria-label={t('landing.showcase.label')}
          className="mx-auto flex w-max min-w-full justify-start gap-1 sm:justify-center"
          onKeyDown={onKeyDown}
        >
          {SCREENS.map(({ id, icon: Icon }, index) => {
            const selected = index === active;
            return (
              <button
                key={id}
                ref={(node) => { tabRefs.current[index] = node; }}
                type="button"
                role="tab"
                id={`showcase-tab-${id}`}
                aria-selected={selected}
                aria-controls="showcase-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => select(index)}
                className={`lp-focus relative inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors duration-200 ${
                  selected ? 'bg-ink text-white' : 'text-[#222] hover:bg-black/5'
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(`landing.showcase.tabs.${id}`)}
                {selected && running && (
                  <span
                    key={`progress-${active}`}
                    className="lp-tab-progress"
                    style={{ '--lp-tab-duration': `${AUTOPLAY_SECONDS}s` }}
                    onAnimationEnd={() => select((active + 1) % SCREENS.length, { manual: false })}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id="showcase-panel"
        role="tabpanel"
        aria-labelledby={`showcase-tab-${SCREENS[active].id}`}
        className="lp-backdrop mt-2 aspect-video lg:aspect-[1380/760]"
      >
        {SCREENS.map(({ id }, index) => {
          if (!loaded.has(index)) return null;
          const image = shot(`hero-${id}`, i18n.language);
          const selected = index === active;
          return (
            <img
              key={`${id}-${image.src}`}
              src={image.src}
              width={image.width}
              height={image.height}
              alt={selected ? t(`landing.showcase.alt.${id}`) : ''}
              aria-hidden={selected ? undefined : 'true'}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchpriority={index === 0 ? 'high' : undefined}
              decoding="async"
              className={`absolute left-1/2 top-[6%] w-[92%] -translate-x-1/2 rounded-t-md shadow-[0_24px_60px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/10 transition-opacity duration-500 ease-out sm:w-[86%] lg:top-[9%] lg:w-[85%] ${
                selected ? 'opacity-100' : 'opacity-0'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="pb-[72px] pt-8 sm:pt-10 lg:pb-24" aria-labelledby="hero-title">
      <Container className="text-center">
        <div>
          <p className="mx-auto inline-flex max-w-full flex-wrap items-center justify-center gap-x-1 rounded-lg bg-sand-100 px-3 py-2 text-sm leading-5 text-ink">
            <span>{t('landing.hero.badge')}</span>
            <a href="#platform" className="lp-focus rounded underline underline-offset-2 hover:text-black/70">
              {t('landing.hero.badgeLink')}
            </a>
          </p>
          <p className="lp-eyebrow mt-8 text-black/60">{t('landing.hero.eyebrow')}</p>
          <h1 id="hero-title" className="lp-h1 mx-auto mt-3 max-w-[860px]">
            {t('landing.hero.title')}
          </h1>
          <p className="mx-auto mt-6 max-w-[500px] text-[17px] leading-[1.4] text-ink">
            {t('landing.hero.description')}
          </p>
          <div className="mt-7 flex justify-center">
            <PillLink to="/register">{t('landing.cta.getStarted')}</PillLink>
          </div>
          <p className="mt-4 text-balance text-sm text-black/60">{t('landing.hero.note')}</p>
        </div>
      </Container>

      <Container className="mt-14 lg:mt-20">
        <Reveal delay={120}>
          <ProductShowcase />
        </Reveal>
      </Container>
    </section>
  );
}
